import { getCareBaseUrl } from '@/services/runtimeConfig';

export { getCareBaseUrl } from '@/services/runtimeConfig';

type CareRouteVariant = 'prod' | 'lower';

type RouteDefinition = string | Record<CareRouteVariant, string>;

type CareRouteKey =
    | 'accountCenterDeleteAccount'
    | 'accountCenterBillingInformation'
    | 'accountCenterHome'
    | 'accountCenterLogin'
    | 'accountCredentials'
    | 'accountCredentialsForgotPassword'
    | 'accountManage'
    | 'accountRegistration'
    | 'billing'
    | 'canadaAccountSignIn'
    | 'flepzSetupCredentials'
    | 'forgotPassword'
    | 'loginView'
    | 'makePayment'
    | 'manageAccount'
    | 'refreshRadio'
    | 'refreshSignal'
    | 'streamingOrganicCreds'
    | 'streamingPurchase'
    | 'streamingSelfPayOrganic'
    | 'targetedOemTrial'
    | 'teslaSubscribe'
    | 'transferSubscription';

const PROD_CARE_HOSTS = new Set(['care.siriusxm.com']);

const CARE_ROUTES: Record<CareRouteKey, RouteDefinition> = {
    accountCenterBillingInformation: '/accountcenter/billing-information',
    accountCenterDeleteAccount: '/accountcenter/delete/delete-account',
    accountCenterHome: '/accountcenter/home',
    accountCenterLogin: '/accountcenter/login?',
    accountCredentials: '/account/credentials',
    accountCredentialsForgotPassword: '/account/credentials/forgot-password?',
    accountManage: '/account/manage',
    accountRegistration: '/account/registration',
    billing: '/accountcenter/billing-information',
    canadaAccountSignIn: '/myaccount',
    flepzSetupCredentials: '/phx-wc/onboarding/setup-credentials/lookup',
    forgotPassword: '/forgot-password',
    loginView: '/login_view.action',
    makePayment: '/account/pay/make-payment',
    manageAccount: '/accountcenter/home',
    refreshRadio: '/refresh',
    refreshSignal: '/subscribe/refresh-signal',
    streamingOrganicCreds: '/subscribe/checkout/purchase/streaming/organic/creds',
    streamingPurchase: '/subscribe/checkout/purchase/streaming/self-pay/organic?promoCode=TA3MOFREE',
    streamingSelfPayOrganic: '/subscribe/checkout/purchase/streaming/self-pay/organic',
    targetedOemTrial: {
        prod: '/activate/trial/targeted-oem',
        lower: '/phx/activate/trial/targeted-oem',
    },
    teslaSubscribe: '/acquisitions-self-pay/ingest/vehicle?vehicleMake=Tesla&promoCode=TESLA24FOR10',
    transferSubscription: {
        prod: '/transfer/radio/login',
        lower: '/phx/transfer/radio/login',
    },
};

function normalizePath(path: string): string {
    return path.startsWith('/') ? path : `/${path}`;
}

function normalizeBaseUrl(baseUrl: string): string {
    return baseUrl.trim().replace(/\/+$/, '');
}

function getCareRouteVariant(careBaseUrl: string): CareRouteVariant {
    if (!careBaseUrl) {
        return 'prod';
    }

    const normalizedBaseUrl = normalizeBaseUrl(careBaseUrl);

    try {
        const hostname = new URL(normalizedBaseUrl).hostname.toLowerCase();
        return PROD_CARE_HOSTS.has(hostname) ? 'prod' : 'lower';
    } catch {
        return normalizedBaseUrl.includes('care.siriusxm.com') ? 'prod' : 'lower';
    }
}

function resolveRoutePath(route: CareRouteKey, careBaseUrl: string): string {
    const routeDefinition = CARE_ROUTES[route];
    return typeof routeDefinition === 'string' ? routeDefinition : routeDefinition[getCareRouteVariant(careBaseUrl)];
}

function appendQueryParams(path: string, queryParams?: Record<string, string | undefined>): string {
    if (!queryParams) {
        return path;
    }

    const entries = Object.entries(queryParams).filter(([, value]) => value && value.trim().length > 0);

    if (entries.length === 0) {
        return path;
    }

    const [pathWithoutHash, hash = ''] = path.split('#');
    const [pathname, existingQuery = ''] = pathWithoutHash.split('?');
    const searchParams = new URLSearchParams(existingQuery);

    for (const [key, value] of entries) {
        searchParams.set(key, value as string);
    }

    const query = searchParams.toString();
    const resolvedPath = query ? `${pathname}?${query}` : pathname;

    return hash ? `${resolvedPath}#${hash}` : resolvedPath;
}

export function buildCareUrl(path: string): string {
    return buildCareUrlFromBaseUrl(getCareBaseUrl(), path);
}

export function buildCareUrlFromBaseUrl(careBaseUrl: string, path: string): string {
    const normalizedPath = normalizePath(path);
    const normalizedBaseUrl = normalizeBaseUrl(careBaseUrl);

    return normalizedBaseUrl ? `${normalizedBaseUrl}${normalizedPath}` : normalizedPath;
}

export function buildCareRouteUrl(
    route: CareRouteKey,
    options?: {
        queryParams?: Record<string, string | undefined>;
    },
): string {
    return buildCareRouteUrlFromBaseUrl(getCareBaseUrl(), route, options);
}

export function buildCareRouteUrlFromBaseUrl(
    careBaseUrl: string,
    route: CareRouteKey,
    options?: {
        queryParams?: Record<string, string | undefined>;
    },
): string {
    return buildCareUrlFromBaseUrl(careBaseUrl, appendQueryParams(resolveRoutePath(route, careBaseUrl), options?.queryParams));
}
